// The client portal — what a CLIENT sees, under /portal. docs/CLIENT_PORTAL.md §4.
//
// Mounted in App.js next to /invoice/:token and /estimate/:token, outside
// ProtectedRoute: it has nothing to do with staff sign-in, and a staff token in
// this browser is never sent (portalApi.js). Who is signed in is whatever
// GET /portal/me says; a 401 from anything means "signed out", and the person
// is taken to the sign-in page with the reason.
//
// Invoices and estimates open on their own public pages (/invoice/:token,
// /estimate/:token) — the page and PDF the email links to, with Pay — so the
// portal lists, totals and exports, and adds no second way to pay.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { FwButton, Notice, Skeleton } from '../../ui/fw';
import { get, post } from './portalApi';
import { hasBilling } from './portalShared';
import SignIn from './screens/SignIn';
import Home from './screens/Home';
import Invoices from './screens/Invoices';
import Estimates from './screens/Estimates';
import Statements from './screens/Statements';
import Account from './screens/Account';
import './Portal.css';

// The server ends an ordinary session after 30 minutes without a request
// (utils/portalAuth.js IDLE_MINUTES). The page says so five minutes before,
// so nobody loses a statement they were halfway through reading.
export const IDLE_MS = 30 * 60 * 1000;
export const WARN_MS = 25 * 60 * 1000;
// Read through here so a test can move time without waiting 30 minutes.
export const idleClock = { now: () => Date.now(), tickMs: 15000 };

export const NAV = [
  { to: '/portal', label: 'Home', end: true },
  { to: '/portal/invoices', label: 'Invoices', billing: true },
  { to: '/portal/estimates', label: 'Estimates' },
  { to: '/portal/statements', label: 'Statements', billing: true },
  { to: '/portal/account', label: 'Account' },
];

function PortalShell({ me, onSignedOut, children }) {
  const navigate = useNavigate();
  const billing = hasBilling(me);
  const current = (me.sessions || []).find((s) => s.current);
  const remembered = !!(current && current.remembered);
  const lastContact = useRef(idleClock.now());
  const [idleWarn, setIdleWarn] = useState(false);

  useEffect(() => {
    if (remembered) return undefined;
    const tick = setInterval(() => {
      const idle = idleClock.now() - lastContact.current;
      if (idle >= IDLE_MS) onSignedOut('You were signed out after 30 minutes without activity.');
      else setIdleWarn(idle >= WARN_MS);
    }, idleClock.tickMs);
    return () => clearInterval(tick);
  }, [remembered, onSignedOut]);

  // Any answer from the server resets its idle clock, so ours follows it.
  useEffect(() => {
    const onContact = () => { lastContact.current = idleClock.now(); setIdleWarn(false); };
    window.addEventListener('portal:contact', onContact);
    return () => window.removeEventListener('portal:contact', onContact);
  }, []);

  const stay = async () => {
    try { await get('/portal/me'); } catch (e) {
      if (e.status === 401) onSignedOut('Your session ended. Please sign in again.');
    }
  };
  const signOut = async () => {
    try { await post('/portal/auth/logout'); } catch (_) { /* signed out either way */ }
    onSignedOut(null);
    navigate('/portal/sign-in', { replace: true });
  };

  const company = me.company || {};
  return (
    <div className="fw-app pt" data-screen="portal">
      <header className="pt-top">
        <div className="pt-top__brand">
          {company.logo ? <img className="pt-top__logo" src={company.logo} alt="" /> : null}
          <span className="pt-top__name">{company.name || 'Your account'}</span>
        </div>
        <div className="pt-top__who">
          <span className="pt-top__email" title={me.email}>{me.email}</span>
          <FwButton variant="quiet" onClick={signOut} data-action="sign-out">Sign out</FwButton>
        </div>
      </header>
      <nav className="pt-nav" aria-label="Your account">
        {NAV.filter((n) => billing || !n.billing).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `pt-nav__link${isActive ? ' pt-nav__link--on' : ''}`}>
            {n.label}
          </NavLink>
        ))}
      </nav>
      {idleWarn ? (
        <div className="pt-body pt-body--banner">
          <Notice tone="warn" title="You will be signed out soon">
            <span>For your security you are signed out after 30 minutes without activity. </span>
            <FwButton variant="ghost" onClick={stay} data-action="stay-signed-in">Stay signed in</FwButton>
          </Notice>
        </div>
      ) : null}
      <main className="pt-body">{children}</main>
    </div>
  );
}

export default function PortalApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(undefined);     // undefined loading · null signed out · object
  const [reason, setReason] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const m = await get('/portal/me');
      setMe(m);
    } catch (e) {
      if (e.status === 401) setMe(null);
      else { setLoadError(e.message); setMe(null); }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSignedOut = useCallback((why) => {
    setMe(null);
    setReason(why || null);
    navigate('/portal/sign-in', { replace: true });
  }, [navigate]);

  // Any screen's 401 is a sign-out, said once, at the sign-in page.
  const onUnauthorized = useCallback(() => onSignedOut('Your session ended. Please sign in again.'), [onSignedOut]);
  const screenProps = useMemo(() => ({ me, onUnauthorized }), [me, onUnauthorized]);

  const onSign = location.pathname.replace(/\/+$/, '') === '/portal/sign-in';
  if (me === undefined && !onSign) {
    return <div className="fw-app pt" data-screen="portal-loading"><main className="pt-body"><Skeleton lines={4} /></main></div>;
  }

  return (
    <Routes>
      <Route path="sign-in" element={<SignIn reason={reason} loadError={loadError} signedIn={!!me} onSignedIn={async () => { setReason(null); await load(); navigate('/portal', { replace: true }); }} />} />
      <Route
        path="*"
        element={!me ? <Navigate to="/portal/sign-in" replace /> : (
          <PortalShell me={me} onSignedOut={onSignedOut}>
            <Routes>
              <Route index element={<Home {...screenProps} />} />
              <Route path="invoices" element={hasBilling(me) ? <Invoices {...screenProps} /> : <Navigate to="/portal" replace />} />
              <Route path="estimates" element={<Estimates {...screenProps} />} />
              <Route path="statements" element={hasBilling(me) ? <Statements {...screenProps} /> : <Navigate to="/portal" replace />} />
              <Route path="account" element={<Account {...screenProps} onChanged={load} onSignedOut={onSignedOut} />} />
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </Routes>
          </PortalShell>
        )}
      />
    </Routes>
  );
}
